import dayjs from 'dayjs';
import { OpeningModeEnum, RampOpeningHoursByDateRangeOutDTO, ReservationCalendarGroupedOutDTO } from '@api/ringil-api.v2';
import { isNotNullOrUndefined } from '@utils/utils';
import { formatDateTimeInTzForFullCalendar } from '@utils/dateWithTz';

// FullCalendar does not render cross-day timed events that start too close to midnight
// (the block is positioned off the bottom of the grid). Shift the display start earlier so it remains visible.
const HACK_NEAR_MIDNIGHT_THRESHOLD_MINUTES = 5; // trigger: start within last 5 min of day (23:55–23:59)
const HACK_NEAR_MIDNIGHT_EXTRA_SHIFT_MINUTES = 3; // shift this many extra minutes before the threshold → display start = 23:52

const hackShiftNearMidnightCrossDayStart = (rawStart: string, end: string): string => {
  const startDjs = dayjs(rawStart);
  const endDjs = dayjs(end);
  const crossesMidnight = endDjs.format('YYYY-MM-DD') > startDjs.format('YYYY-MM-DD');
  const minutesToMidnight = 1440 - (startDjs.hour() * 60 + startDjs.minute());
  if (crossesMidnight && minutesToMidnight <= HACK_NEAR_MIDNIGHT_THRESHOLD_MINUTES) {
    const shiftedMinute = 1440 - HACK_NEAR_MIDNIGHT_THRESHOLD_MINUTES - HACK_NEAR_MIDNIGHT_EXTRA_SHIFT_MINUTES;
    const h = Math.floor(shiftedMinute / 60).toString().padStart(2, '0');
    const m = (shiftedMinute % 60).toString().padStart(2, '0');
    return `${rawStart.substring(0, 10)}T${h}:${m}`;
  }
  return rawStart;
};

/** Reservations grouped per ramp → flat FullCalendar events. */
export const transformReservationData = (res: ReservationCalendarGroupedOutDTO[]) =>
  res.flatMap(t =>
    t.reservations.map(t2 => {
      const rawStart =
        isNotNullOrUndefined(t2.timeslot.timezoneId) && isNotNullOrUndefined(t2.timeslot.utcStart)
          ? formatDateTimeInTzForFullCalendar(t2.timeslot.utcStart, t2.timeslot.timezoneId)
          : formatDateTimeInTzForFullCalendar(t2.timeslot.since, 'Europe/Prague');
      const end =
        isNotNullOrUndefined(t2.timeslot.timezoneId) && isNotNullOrUndefined(t2.timeslot.utcEnd)
          ? formatDateTimeInTzForFullCalendar(t2.timeslot.utcEnd, t2.timeslot.timezoneId)
          : formatDateTimeInTzForFullCalendar(t2.timeslot.till, 'Europe/Prague');
      const start = rawStart && end ? hackShiftNearMidnightCrossDayStart(rawStart, end) : rawStart;
      return {
        ...t2,
        resourceId: t.rampId,
        start,
        end,
        id: t2.reservationId,
        title: `${t2.reservationId}`,
        color: t2.labels?.length === 1 ? t2.labels[0].labelColor : '',
        labels: t2.labels,
        rampId: t.rampId,
      };
    }),
  );

/** Closed intervals → background events painted over the grid. */
export const transformClosingHoursData = (data: RampOpeningHoursByDateRangeOutDTO[]) => {
  const arr: any = [];

  data?.forEach(item => {
    const closedHours = item.dailyInverseOpeningHours;

    closedHours.forEach(day => {
      const isClosed = day.openingMode === OpeningModeEnum.CLOSED;
      const intervals = day.intervals;
      const dayDate = dayjs(day.date).day();

      const baseEventObject = {
        daysOfWeek: [dayDate],
        display: 'background',
        overlap: false,
        backgroundColor: 'var(--complementary-errormedium)',
        resourceId: item.rampId,
      };

      if (isClosed) {
        intervals.forEach(int => {
          arr.push({
            startTime: int.since,
            endTime: int.till,
            ...baseEventObject,
          });
        });
      }
    });
  });
  return arr;
};

/** Resource (ramp column) order: by timezone offset, then location name, then the location's manual ramp order. */
export const sortResourcesByTimezoneOffset = (a: any, b: any): number => {
  // FullCalendar puts custom properties at the top level, not in extendedProps
  const offsetA = a.timezoneOffset ?? -1;
  const offsetB = b.timezoneOffset ?? -1;
  const locationA = a.locationName ?? '';
  const locationB = b.locationName ?? '';

  // Put -1 (no timezone info) at the end
  if (offsetA === -1 && offsetB !== -1) return 1;
  if (offsetA !== -1 && offsetB === -1) return -1;

  if (offsetA !== offsetB) return offsetA - offsetB;

  const ci = { sensitivity: 'base' } as const;
  const locCmp = locationA.localeCompare(locationB, undefined, ci);
  if (locCmp !== 0) return locCmp;

  // Manual per-location ramp ordering (`orderIndex`) wins over the ramp name; ramps with no rank
  // (location list not loaded yet, foreign ramp) fall back to the name and sort last.
  const orderA = a.rampOrder ?? a.extendedProps?.rampOrder ?? Number.MAX_SAFE_INTEGER;
  const orderB = b.rampOrder ?? b.extendedProps?.rampOrder ?? Number.MAX_SAFE_INTEGER;
  if (orderA !== orderB) return orderA - orderB;

  return a.title.localeCompare(b.title, undefined, ci);
};